// client/src/components/pages/Settings/LexiStarWebhooks/LexiStarWebhooks.tsx
//
// Settings → Integrations & webhooks, /settings/modules/monitoring/lexistar-webhooks
// (ST-00207; M4b contracts §3.2). Page permission lexistar.integrations.view; every change
// needs lexistar.integrations.manage (tenant administrators).
//
//   Webhooks     the organisation's subscriptions: endpoint, events, state (active / paused
//                and why), last delivery, what is queued; Send test (PING), Pause / Resume,
//                Edit, New secret (rotate, shown once), Delete.
//   Deliveries   the 30-day delivery log: event, state (queued, retrying n of 6, delivered,
//                dead, failed), response, attempts, next attempt; Details (the exact body and
//                the stored event) and Replay (a new delivery of the same event). Refreshes
//                while something is about to be sent.
//
// The signing secret is shown once — after creating a webhook and after New secret — in
// SecretDialog, with Copy. Help: settings/set-up-webhooks.
//
// ST-00280: a delivery's event is shown in words (utils/eventTypeLabels); its code — the
// payload's `type` — is the tooltip in the log and the second line in Details.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  FaCopy,
  FaExclamationTriangle,
  FaKey,
  FaPause,
  FaPaperPlane,
  FaPen,
  FaPlay,
  FaPlug,
  FaPlus,
  FaRedo,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import {
  PageHeader,
  ThemedSelect,
  UnifiedDateRangePicker,
  useAuth,
} from '../../../../types/commonComponents';
import {
  formatMiningDateTime,
  formatRelativeTime,
  toApiDateString,
} from '../../../../utils/dateUtils';
import type {
  WebhookDeliveryDetail,
  WebhookDeliveryState,
  WebhookDeliverySummary,
  WebhookSubscription,
  WebhookSubscriptionWithSecret,
} from '../../../../types/lexistar-webhooks';
import SubscriptionDialog from './SubscriptionDialog';
import {
  deleteSubscription,
  errorText,
  getDelivery,
  listDeliveries,
  listSubscriptions,
  replayDelivery,
  resumeSubscription,
  rotateSecret,
  sendTest,
  updateSubscription,
} from './webhooksApi';
import {
  STATE_FILTERS,
  deliveryState,
  describePatterns,
  endpointLabel,
  kindLabel,
  responseText,
  type StateTone,
} from './webhookLogic';
import { eventTypeLabel } from '../../../../utils/eventTypeLabels';

export const VIEW = 'lexistar.integrations.view';
export const MANAGE = 'lexistar.integrations.manage';
const PAGE_SIZE = 50;
const POLL_MS = 5000;
const DAY_MS = 86_400_000;

const card = 'bg-surface rounded-xl border border-slate-200 shadow-sm';
const buttonClass =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50';

const TONE_CLASS: Record<StateTone, string> = {
  good: 'border border-emerald-200 bg-emerald-50 text-emerald-800',
  wait: 'border border-dashed border-slate-400 text-slate-600',
  warn: 'border border-amber-200 bg-amber-50 text-amber-800',
  bad: 'border border-red-200 bg-red-50 text-red-700',
};

function Chip({ tone, glyph, children }: { tone: StateTone; glyph: string; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${TONE_CLASS[tone]}`}
    >
      <span aria-hidden="true">{glyph}</span>
      {children}
    </span>
  );
}

function Banner({
  tone,
  children,
  onDismiss,
}: {
  tone: 'error' | 'notice' | 'warning';
  children: ReactNode;
  onDismiss?: () => void;
}) {
  const cls =
    tone === 'error'
      ? 'bg-red-50 border-red-200 text-red-700'
      : tone === 'warning'
        ? 'bg-amber-50 border-amber-200 text-amber-800'
        : 'bg-emerald-50 border-emerald-200 text-emerald-800';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mb-4 px-4 py-3 rounded-lg border flex items-start gap-3 text-sm ${cls}`}
    >
      {tone !== 'notice' && (
        <FaExclamationTriangle className="mt-0.5 flex-shrink-0" aria-hidden="true" />
      )}
      <div className="flex-1">{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="opacity-60 hover:opacity-100"
        >
          <FaTimes size={12} />
        </button>
      )}
    </div>
  );
}

function useEscape(onClose: () => void): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
}

// ── the secret, shown once ──────────────────────────────────────────────────

function SecretDialog({
  result,
  rotated,
  onClose,
}: {
  result: WebhookSubscriptionWithSecret;
  rotated: boolean;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<'yes' | 'no' | null>(null);
  const codeRef = useRef<HTMLElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  useEffect(() => doneRef.current?.focus(), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.secret);
      setCopied('yes');
    } catch {
      // No clipboard permission: select the text so Ctrl+C works.
      const el = codeRef.current;
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
      setCopied('no');
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="webhook-secret-title"
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
      >
        <div className="px-6 pt-5 pb-4 space-y-3">
          <h2
            id="webhook-secret-title"
            className="text-lg font-bold text-slate-900 flex items-center gap-2"
          >
            <FaKey className="text-primary-500" aria-hidden="true" />
            {rotated ? 'New signing secret' : 'Webhook created: your signing secret'}
          </h2>
          <p className="text-sm text-slate-600">
            Store this secret in the system that receives{' '}
            <strong>{result.subscription.name}</strong>. It checks the
            <code className="mx-1 text-xs">X-LexiStar-Signature</code> header with it.{' '}
            <strong>This is the only time LexiStar shows it.</strong>
            {rotated &&
              ' The old secret no longer works: deliveries that fail meanwhile are retried with the new one.'}
          </p>
          <div className="flex items-stretch gap-2">
            <code
              ref={codeRef}
              data-testid="webhook-secret"
              className="flex-1 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 font-mono text-sm text-slate-900 break-all select-all"
            >
              {result.secret}
            </code>
            <button
              type="button"
              onClick={copy}
              className="px-3 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1.5"
            >
              <FaCopy aria-hidden="true" /> Copy
            </button>
          </div>
          <p className="text-xs text-slate-500 min-h-[1rem]" aria-live="polite">
            {copied === 'yes'
              ? 'Copied.'
              : copied === 'no'
                ? 'Selected: press Ctrl+C (⌘C) to copy.'
                : ''}
          </p>
        </div>
        <div className="flex justify-end px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            ref={doneRef}
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm bg-primary-500 hover:bg-primary-600"
          >
            I have stored the secret
          </button>
        </div>
      </div>
    </div>
  );
}

// ── confirm ─────────────────────────────────────────────────────────────────

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
  danger: boolean;
  run: () => Promise<void>;
}

function ConfirmDialog({ state, onClose }: { state: ConfirmState; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEscape(onClose);
  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await state.run();
      onClose();
    } catch (err) {
      setError(errorText(err, 'That did not work.'));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="webhook-confirm-title"
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      >
        <div className="p-6 space-y-2">
          <h2 id="webhook-confirm-title" className="text-lg font-bold text-slate-900">
            {state.title}
          </h2>
          <p className="text-sm text-slate-600 leading-relaxed">{state.message}</p>
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={go}
            disabled={busy}
            className={`px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm disabled:opacity-60 ${state.danger ? 'bg-red-500 hover:bg-red-600' : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {busy ? 'Working…' : state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── one delivery ────────────────────────────────────────────────────────────

function DeliveryDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const [detail, setDetail] = useState<WebhookDeliveryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEscape(onClose);
  useEffect(() => {
    let live = true;
    getDelivery(id)
      .then((d) => live && setDetail(d))
      .catch((err) => live && setError(errorText(err, 'The delivery could not be loaded.')));
    return () => {
      live = false;
    };
  }, [id]);
  const state = detail ? deliveryState(detail) : null;
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="webhook-delivery-title"
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-5">
          <h2 id="webhook-delivery-title" className="text-lg font-bold text-slate-900">
            Delivery{' '}
            {detail ? <span className="font-mono text-sm text-slate-500">{detail.id}</span> : null}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-slate-400 hover:text-slate-600"
          >
            <FaTimes />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4 text-sm">
          {error && <Banner tone="error">{error}</Banner>}
          {!detail && !error && <p className="text-slate-500">Loading…</p>}
          {detail && state && (
            <>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                <div>
                  <dt className="text-xs text-slate-500">Event</dt>
                  {/* ST-00280: in words; the code is the payload's `type` */}
                  <dd>
                    {eventTypeLabel(detail.event_type)}
                    <span className="block font-mono text-xs text-slate-500">{detail.event_type}</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">State</dt>
                  <dd>
                    <Chip tone={state.tone} glyph={state.glyph}>
                      {state.label}
                    </Chip>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Last response</dt>
                  <dd>{responseText(detail)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Attempts</dt>
                  <dd className="tabular-nums">{detail.attempt}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Queued</dt>
                  <dd>{formatMiningDateTime(detail.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">
                    {detail.state === 'delivered' ? 'Delivered' : 'Next attempt'}
                  </dt>
                  <dd>
                    {detail.state === 'delivered'
                      ? formatMiningDateTime(detail.delivered_at)
                      : detail.next_attempt_at
                        ? formatMiningDateTime(detail.next_attempt_at)
                        : 'None'}
                  </dd>
                </div>
                {detail.replay_of && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-slate-500">Replay of</dt>
                    <dd className="font-mono text-xs">{detail.replay_of}</dd>
                  </div>
                )}
              </dl>
              <div>
                <p className="text-xs text-slate-500 mb-1">
                  The body your endpoint receives (every attempt carries a new{' '}
                  <code>X-LexiStar-Timestamp</code> and signature):
                </p>
                <pre
                  className="max-h-80 overflow-auto rounded-lg bg-slate-900 text-slate-100 text-xs p-3"
                  data-testid="webhook-envelope"
                >
                  {JSON.stringify(detail.envelope, null, 2)}
                </pre>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── the page ────────────────────────────────────────────────────────────────

interface Filters {
  subscriptionId: string;
  state: '' | WebhookDeliveryState;
  start: Date;
  end: Date;
}

/** SAST day bounds as instants: [start 00:00, end + 1 day 00:00). */
function rangeOf(start: Date, end: Date): { from: string; to: string } {
  const sast = (d: Date) => new Date(`${toApiDateString(d)}T00:00:00+02:00`);
  return {
    from: sast(start).toISOString(),
    to: new Date(sast(end).getTime() + DAY_MS).toISOString(),
  };
}

export default function LexiStarWebhooks() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(MANAGE);
  const canView = canManage || hasPermission(VIEW);

  const [subs, setSubs] = useState<WebhookSubscription[]>([]);
  const [keyConfigured, setKeyConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>(() => {
    const end = new Date();
    return { subscriptionId: '', state: '', start: new Date(end.getTime() - 6 * DAY_MS), end };
  });
  const [deliveries, setDeliveries] = useState<WebhookDeliverySummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);
  const [deliveriesError, setDeliveriesError] = useState<string | null>(null);

  const [dialog, setDialog] = useState<{ subscription: WebhookSubscription | null } | null>(null);
  const [secret, setSecret] = useState<{
    result: WebhookSubscriptionWithSecret;
    rotated: boolean;
  } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadSubs = useCallback(async () => {
    try {
      setError(null);
      const res = await listSubscriptions();
      setSubs(res.subscriptions);
      setKeyConfigured(res.key_configured);
    } catch (err) {
      setError(errorText(err, 'The webhooks could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDeliveries = useCallback(
    async (offset = 0, quiet = false) => {
      if (!quiet) setLoadingDeliveries(true);
      try {
        setDeliveriesError(null);
        const res = await listDeliveries({
          ...(filters.subscriptionId ? { subscription_id: filters.subscriptionId } : {}),
          ...(filters.state ? { state: filters.state } : {}),
          ...rangeOf(filters.start, filters.end),
          limit: PAGE_SIZE,
          offset,
        });
        setDeliveries((prev) => (offset === 0 ? res.deliveries : [...prev, ...res.deliveries]));
        setHasMore(res.has_more);
      } catch (err) {
        setDeliveriesError(errorText(err, 'The delivery log could not be loaded.'));
      } finally {
        setLoadingDeliveries(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    if (canView) void loadSubs();
  }, [canView, loadSubs]);

  useEffect(() => {
    if (canView) void loadDeliveries(0);
  }, [canView, loadDeliveries]);

  // Refresh while something is about to go out (a queued delivery, a retry within a minute).
  const soon = useMemo(
    () =>
      deliveries.some(
        (d) =>
          d.state === 'pending' &&
          (!d.next_attempt_at || Date.parse(d.next_attempt_at) - Date.now() < 60_000)
      ),
    [deliveries]
  );
  useEffect(() => {
    if (!soon || !canView) return undefined;
    const t = setTimeout(() => {
      void loadDeliveries(0, true);
      void loadSubs();
    }, POLL_MS);
    return () => clearTimeout(t);
  }, [soon, canView, deliveries, loadDeliveries, loadSubs]);

  const refreshAll = async () => {
    await Promise.all([loadSubs(), loadDeliveries(0, true)]);
  };

  const act = async (id: string, fn: () => Promise<string>) => {
    setBusyId(id);
    setError(null);
    try {
      setNotice(await fn());
      await refreshAll();
    } catch (err) {
      setError(errorText(err, 'That did not work.'));
    } finally {
      setBusyId(null);
    }
  };

  const onTest = (s: WebhookSubscription) =>
    act(s.id, async () => {
      await sendTest(s.id);
      return `A test delivery (PING) to ${s.name} is queued. Its result appears in the delivery log in a few seconds.`;
    });
  const onPause = (s: WebhookSubscription) =>
    act(s.id, async () => {
      await updateSubscription(s.id, { active: false });
      return `${s.name} is paused: new events are not sent to it until you resume it.`;
    });
  const onResume = (s: WebhookSubscription) =>
    act(s.id, async () => {
      await resumeSubscription(s.id);
      return `${s.name} is active again. Deliveries that were waiting go out now.`;
    });
  const onReplay = (d: WebhookDeliverySummary) =>
    act(d.id, async () => {
      const r = await replayDelivery(d.id);
      return r.waits_for_resume
        ? `The event is queued again, but ${d.subscription_name || 'the webhook'} is paused: it goes out once you resume it.`
        : 'The event is queued again as a new delivery.';
    });
  const onRotate = (s: WebhookSubscription) =>
    setConfirm({
      title: 'Replace the signing secret?',
      message: `${s.name} gets a new secret now, and the old one stops working at once. Update your endpoint straight after: deliveries it rejects meanwhile are retried.`,
      confirmLabel: 'Replace secret',
      danger: false,
      run: async () => {
        const result = await rotateSecret(s.id);
        setSecret({ result, rotated: true });
        await refreshAll();
      },
    });
  const onDelete = (s: WebhookSubscription) =>
    setConfirm({
      title: `Delete ${s.name}?`,
      message:
        'No more events are sent to this endpoint and its secret is destroyed. Deliveries still queued fail. The delivery log stays readable for 30 days.',
      confirmLabel: 'Delete webhook',
      danger: true,
      run: async () => {
        await deleteSubscription(s.id);
        setNotice(`${s.name} is deleted.`);
        if (filters.subscriptionId === s.id) setFilters((f) => ({ ...f, subscriptionId: '' }));
        await refreshAll();
      },
    });

  const header = (
    <PageHeader
      icon={FaPlug}
      title="Integrations & webhooks"
      subtitle="Send LexiStar events to your own systems as signed HTTPS requests, with retries and a delivery log."
      actions={
        canView && canManage ? (
          <button
            type="button"
            onClick={() => setDialog({ subscription: null })}
            disabled={!keyConfigured}
            title={keyConfigured ? undefined : 'Webhooks are not configured on this server yet'}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 shadow-sm disabled:opacity-60"
          >
            <FaPlus size={12} aria-hidden="true" /> New webhook
          </button>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
          <Banner tone="warning">
            You do not have permission to view integrations and webhooks.
          </Banner>
        </div>
      </div>
    );
  }

  const subName = (id: string) => subs.find((s) => s.id === id)?.name;

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 space-y-6">
        {!keyConfigured && canManage && (
          <Banner tone="warning">
            Webhooks are not configured on this server yet: new webhooks and new secrets cannot be
            made. Ask your administrator to set
            <code className="mx-1">LEXISTAR_WEBHOOK_SECRET_KEY</code> for the server and
            lexistar-connectors.
          </Banner>
        )}
        {error && (
          <Banner tone="error" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {notice && (
          <Banner tone="notice" onDismiss={() => setNotice(null)}>
            {notice}
          </Banner>
        )}

        {/* Webhooks */}
        <section className={card} aria-labelledby="webhooks-heading">
          <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-slate-100">
            <h2 id="webhooks-heading" className="text-base font-semibold text-slate-900">
              Webhooks
            </h2>
            <button
              type="button"
              onClick={() => void refreshAll()}
              className={buttonClass}
              aria-label="Refresh"
            >
              <FaSyncAlt size={11} aria-hidden="true" /> Refresh
            </button>
          </div>
          {loading ? (
            <p className="px-5 py-8 text-sm text-slate-500">Loading…</p>
          ) : subs.length === 0 ? (
            <div className="px-5 py-10 text-center space-y-2">
              <p className="text-sm font-medium text-slate-800">No webhooks yet</p>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                A webhook sends LexiStar events, such as a trip being dispatched or a deviation
                opening, to a system of yours as they happen.
              </p>
              {canManage && keyConfigured && (
                <button
                  type="button"
                  onClick={() => setDialog({ subscription: null })}
                  className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600"
                >
                  <FaPlus size={12} aria-hidden="true" /> New webhook
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {subs.map((s) => {
                const st = s.stats;
                const busy = busyId === s.id;
                return (
                  <li key={s.id} className="px-4 sm:px-5 py-4" data-testid={`webhook-${s.id}`}>
                    <div className="flex flex-col lg:flex-row lg:items-start gap-3">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-semibold text-slate-900">{s.name}</h3>
                          {s.active ? (
                            <Chip tone="good" glyph="●">
                              Active
                            </Chip>
                          ) : (
                            <Chip tone="warn" glyph="❚❚">
                              Paused
                            </Chip>
                          )}
                        </div>
                        <p className="text-xs font-mono text-slate-600 break-all">
                          {endpointLabel(s.url)}
                        </p>
                        <p className="text-xs text-slate-600">{describePatterns(s.event_types)}</p>
                        {!s.active && s.paused_reason && (
                          <p className="text-xs text-amber-800">{s.paused_reason}</p>
                        )}
                        <p className="text-xs text-slate-500">
                          {s.last_delivery_at
                            ? `Last delivered ${formatRelativeTime(s.last_delivery_at)}`
                            : 'Nothing delivered yet'}
                          {st
                            ? ` · ${st.delivered_24h} delivered, ${st.dead_24h} dead in 24 h`
                            : ''}
                          {st && st.pending > 0
                            ? ` · ${st.pending} queued${st.retrying > 0 ? ` (${st.retrying} retrying)` : ''}`
                            : ''}
                          {s.consecutive_failures > 0
                            ? ` · ${s.consecutive_failures} failed in a row`
                            : ''}
                        </p>
                      </div>
                      {canManage && (
                        <div
                          className="flex flex-wrap gap-2 lg:justify-end"
                          aria-label={`Actions for ${s.name}`}
                        >
                          <button
                            type="button"
                            className={buttonClass}
                            disabled={busy}
                            onClick={() => void onTest(s)}
                          >
                            <FaPaperPlane size={11} aria-hidden="true" /> Send test
                          </button>
                          {s.active ? (
                            <button
                              type="button"
                              className={buttonClass}
                              disabled={busy}
                              onClick={() => void onPause(s)}
                            >
                              <FaPause size={11} aria-hidden="true" /> Pause
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={buttonClass}
                              disabled={busy}
                              onClick={() => void onResume(s)}
                            >
                              <FaPlay size={11} aria-hidden="true" /> Resume
                            </button>
                          )}
                          <button
                            type="button"
                            className={buttonClass}
                            disabled={busy}
                            onClick={() => setDialog({ subscription: s })}
                          >
                            <FaPen size={11} aria-hidden="true" /> Edit
                          </button>
                          <button
                            type="button"
                            className={buttonClass}
                            disabled={busy || !keyConfigured}
                            onClick={() => onRotate(s)}
                          >
                            <FaKey size={11} aria-hidden="true" /> New secret
                          </button>
                          <button
                            type="button"
                            className={`${buttonClass} text-red-700`}
                            disabled={busy}
                            onClick={() => onDelete(s)}
                          >
                            <FaTrash size={11} aria-hidden="true" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Deliveries */}
        <section className={card} aria-labelledby="deliveries-heading">
          <div className="px-4 sm:px-5 py-3 border-b border-slate-100 space-y-3">
            <h2 id="deliveries-heading" className="text-base font-semibold text-slate-900">
              Delivery log{' '}
              <span className="text-xs font-normal text-slate-500">(kept 30 days)</span>
            </h2>
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              <div className="md:w-64">
                <ThemedSelect
                  value={filters.subscriptionId}
                  onChange={(e) => setFilters((f) => ({ ...f, subscriptionId: e.target.value }))}
                  aria-label="Webhook"
                >
                  <option value="">Every webhook</option>
                  {subs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
              <div className="md:w-56">
                <ThemedSelect
                  value={filters.state}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, state: e.target.value as Filters['state'] }))
                  }
                  aria-label="State"
                >
                  {STATE_FILTERS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>
              <UnifiedDateRangePicker
                startDate={filters.start}
                endDate={filters.end}
                onStartDateChange={(d) => setFilters((f) => ({ ...f, start: d }))}
                onEndDateChange={(d) => setFilters((f) => ({ ...f, end: d }))}
                bounds="past"
              />
            </div>
          </div>
          {deliveriesError && (
            <div className="px-4 sm:px-5 pt-4">
              <Banner tone="error">{deliveriesError}</Banner>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <th scope="col" className="px-4 sm:px-5 py-2 font-medium">
                    Queued
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Event
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium hidden md:table-cell">
                    Webhook
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    State
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium hidden lg:table-cell">
                    Response
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium hidden lg:table-cell">
                    Next attempt
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deliveries.map((d) => {
                  const st = deliveryState(d);
                  const kind = kindLabel(d.kind);
                  return (
                    <tr key={d.id} data-testid={`delivery-${d.id}`}>
                      <td className="px-4 sm:px-5 py-2 whitespace-nowrap tabular-nums text-slate-700">
                        {formatMiningDateTime(d.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs text-slate-800" title={d.event_type}>
                          {eventTypeLabel(d.event_type)}
                        </span>
                        {kind && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-[11px] text-slate-600">
                            {kind}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell text-slate-700">
                        {d.subscription_name || subName(d.subscription_id) || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <Chip tone={st.tone} glyph={st.glyph}>
                          {st.label}
                        </Chip>
                      </td>
                      <td
                        className="px-3 py-2 hidden lg:table-cell text-xs text-slate-600 max-w-xs truncate"
                        title={responseText(d)}
                      >
                        {responseText(d)}
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell text-xs text-slate-600 whitespace-nowrap">
                        {d.state === 'pending' && d.next_attempt_at
                          ? formatMiningDateTime(d.next_attempt_at)
                          : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className={buttonClass}
                            onClick={() => setDetailId(d.id)}
                            aria-label={`Details of delivery ${d.id}`}
                          >
                            <FaSearch size={10} aria-hidden="true" /> Details
                          </button>
                          {canManage && d.state !== 'pending' && (
                            <button
                              type="button"
                              className={buttonClass}
                              disabled={busyId === d.id}
                              onClick={() => void onReplay(d)}
                            >
                              <FaRedo size={10} aria-hidden="true" /> Replay
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loadingDeliveries && deliveries.length === 0 && !deliveriesError && (
            <p className="px-5 py-8 text-sm text-slate-500 text-center">
              No deliveries{' '}
              {filters.subscriptionId
                ? `to ${subName(filters.subscriptionId) || 'this webhook'} `
                : ''}
              in this period.
            </p>
          )}
          {loadingDeliveries && deliveries.length === 0 && (
            <p className="px-5 py-8 text-sm text-slate-500">Loading…</p>
          )}
          {hasMore && (
            <div className="px-5 py-3 border-t border-slate-100 text-center">
              <button
                type="button"
                className={buttonClass}
                onClick={() => void loadDeliveries(deliveries.length)}
              >
                Show more
              </button>
            </div>
          )}
        </section>
      </div>

      {dialog && (
        <SubscriptionDialog
          subscription={dialog.subscription}
          onClose={() => setDialog(null)}
          onCreated={(result) => {
            setDialog(null);
            setSecret({ result, rotated: false });
            setNotice(
              `${result.subscription.name} is created. Send a test to check that your endpoint answers.`
            );
            void refreshAll();
          }}
          onUpdated={(s) => {
            setDialog(null);
            setNotice(`${s.name} is saved.`);
            void refreshAll();
          }}
        />
      )}
      {secret && (
        <SecretDialog
          result={secret.result}
          rotated={secret.rotated}
          onClose={() => setSecret(null)}
        />
      )}
      {confirm && <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />}
      {detailId && <DeliveryDialog id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
